import { Fragment, useState, useContext, useEffect, useCallback } from 'react';
import { Button, Table, FormControl, Row, Col, Form, ButtonGroup } from 'react-bootstrap'

// For Login related items
import { AuthContext } from "./Login/AuthContext"
import { DatabaseContext } from './DatabaseContext';

// Common interface items
import Icons from './common/Icons';
import ProtectedComponent from './common/ProtectedComponent';
import { dateYYYYMMMDD, validatePositiveInt, validateAlphanumeric } from '../services/commonParse';

// Interaction with database
import { updateBPBatch, createCodeList, createXMLnewparts, createXMLqcgrade } from '../services/baseplateService';
import { makeRemoteGlueOptions } from "../services/glueService"
import { makeKaptonOptions } from '../services/kaptonService';
import { makeTapeOptions } from "../services/tapeService"
import { addRemote, updateRemote, deleteRemote } from '../services/remoteService';
import { HGCFormRowText, HGCFormRowDate, HGCFormRowSelect } from './common/SimpleForm';
import { SimpleFold } from './common/misc';
// Additional interface items
import TableViewAdd, { defaultAdd, defaultResetAdd, defaultValidateAdd } from './common/TableViewAdd';
import TableViewEdit, { defaultEdit, defaultReset, defaultValidateEdit } from './common/TableViewEdit';
import PaginateTable, { FuzzyTextFilter, debounce } from './common/TableDisplay';
import { createRowObj, filterTable, findTableRow, tabField } from '../services/tableUtils';

const RemoteColumns = () => [
  { Header: 'Remote', Cell: RemoteCellID, Filter: FuzzyTextFilter((remote) => [remote.remote_desc]) },
  { Header: 'Destination', Cell: RemoteCellDest },
  { Header: 'Date', Cell: (remote) => dateYYYYMMMDD(remote.remote_date) },
  { Header: 'Tracking', accessor: 'stt_idx', Cell: RemoteCellTracking },
  { Header: 'Contents', accessor: 'id', Cell: RemoteCellContents, minWidth: "60%" },
]

const makeBPUpdateForm = (remotetask_id = null, glue_id = null, kapton_id = null, tape_id = null) => {
  const remove = glue_id === null;
  return {
    remotetask_id: remotetask_id,
    glue_id: glue_id,
    kapton_id: kapton_id,
    tape_id: tape_id,
    kapton_align_passed: remove ? null : true,
    defect_lam_glue_hole: remove ? null : 0,
    defect_lam_glue_pad: remove ? null : 0,
    defect_lam_delam_pad: remove ? null : 0,
  }
}

const extractIDs = (remote, baseplateList, kaptonList, tapeList) => {
  const default_return = { glue_id: null, kapton_id: null, tape_id: null, type: "" }
  if (!remote) { return default_return; }
  if (remote.idlist.length === 0) { return default_return; }
  const bp = findTableRow(baseplateList, (bp) => bp.id === remote.idlist[0]);
  if (bp === null) { return default_return; }
  const reType = (bp) => {
    if (bp.tape_id) {
      const tape = findTableRow(tapeList, (t) => t.id === bp.tape_id);
      if (bp.type === "F0W" || bp.type === "F0T") {
        return bp.type[0] + tape.type[1] + bp.type[2];
      } else if (bp.type === "SLW" || bp.type === "SHW") {
        return tape.type[0] + bp.type[1] + bp.type[2];
      }
    }
    if (bp.kapton_id) {
      const kap = findTableRow(kaptonList, (k) => k.id == bp.kapton_id);
      if (bp.type === "SLW" || bp.type === "SHW") {
        return kap.type[0] + bp.type[1] + bp.type[2];
      }
    }
    return bp.type;
  }

  return {
    glue_id: bp.glue_id,
    kapton_id: bp.kapton_id,
    tape_id: bp.tape_id,
    type: reType(bp),
  }
}

const RemoteCellID = (remote) => {
  const id = remote.id;
  const { user } = useContext(AuthContext);
  const { baseplateList, updateBaseplateList, updateRemoteList } = useContext(DatabaseContext);

  // For showing single items
  const [showEdit, setShowEdit] = useState(false);
  const removeAction = async () => {
    const removeList = tabField(filterTable(baseplateList, (bp) => bp.remotetask_id === id), 'id')
    console.log(removeList);
    await updateBPBatch({
      idList: removeList,
      ...makeBPUpdateForm()
    }).then(
      await deleteRemote(id).then(
        async () => {
          updateRemoteList();
          updateBaseplateList();
        }
      )
    )
  }
  return <Fragment>
    {remote.remote_desc}<br />
    <Button variant='light' size='sm' onClick={() => { setShowEdit(true) }}>{Icons.edit}</Button>
    {user.role === "admin" && <Button variant='light' size='sm' onClick={removeAction}>{Icons.remove}</Button>}
    {showEdit && <RemoteEdit id={id} showState={[showEdit, setShowEdit]} />}
  </Fragment>
}

const RemoteCellDest = (remote) => {
  const { baseplateList, kaptonList, tapeList, glueList } = useContext(DatabaseContext);
  const glue = findTableRow(glueList, (glue) => glue.id == extractIDs(remote, baseplateList, kaptonList, tapeList).glue_id);
  return <>{glue ? glue.source : '--'}</>

}

const RemoteCellTracking = (remote) => {
  const makeLink = (stt_idx) => <a href={`https://cmsr-remote.web.cern.ch/tracking_view/${stt_idx}`}> STT {stt_idx} 🔗</a >
  return <Fragment>{remote.stt_idx === null ? "--" : makeLink(remote.stt_idx)}</Fragment>
}

const makeKeyList = (remote) => remote.bare_list.map((v, i) => (v ? v : remote.ws_list[i]));

const RemoteCellContents = (remote) => {
  const { baseplateList, glueList } = useContext(DatabaseContext);

  // Getting the list of baseplates associated with the remote of interest
  const qualityCache = createBareQualityCache(filterTable(baseplateList, (bp) => bp.remotetask_id === remote.id), glueList);
  const keyList = makeKeyList(remote);
  const fullIDList = remote.idlist;
  const estMass = (remote.weight_sum + 2250 + 6 * remote.idlist.length) / 1000; //Adding mass of standard remote box and 6 grams to each item, cast to kg

  // Display for warning messages
  const [warning,] = _validateRemoteList(keyList, qualityCache, remote.id, remote.destination);

  return <Fragment>
    <RemoteCellContentsSummary remote={remote} idList={fullIDList} keyList={keyList} estMass={estMass} />
    <SimpleFold header="Full List">
      <RemoteCellContentsDetail remote={remote} keyList={keyList} warning={warning} />
    </SimpleFold>
    <RemoteCellContentsExport fullList={keyList} fullIDList={fullIDList} remote={remote} />
  </Fragment >
}


const RemoteCellContentsSummary = ({ remote, keyList, estMass }) => {
  const { baseplateList, kaptonList, tapeList } = useContext(DatabaseContext);
  const ids = extractIDs(remote, baseplateList, kaptonList, tapeList);
  return <> Total ({keyList.length} {ids.type}, {estMass.toFixed(1)} kg.) </>
}

const RemoteCellContentsDetail = ({ remote, keyList, warning }) => {
  const { baseplateList, kaptonList, tapeList } = useContext(DatabaseContext);
  const ids = extractIDs(remote, baseplateList, kaptonList, tapeList);
  return <Table bordered striped>
    <tbody>
      <tr><td>All items ({ids.type})</td><td>{keyList.join(", ")}</td></tr>
      {
        Object.keys(warning).map((t, idx) => {
          return warning[t].length > 0 && <tr key={`warning-${idx}`}>
            <td style={{ minWidth: "20em", color: "red" }}>{t}</td>
            <td style={{ textAlign: "left" }}>{warning[t].join(", ")}</td>
          </tr>
        })
      }
    </tbody>
  </Table>
}

const RemoteCellContentsExport = ({ fullList, fullIDList, remote }) => {
  const box = remote.remote_desc;

  return <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5em" }}>
    <ButtonGroup>
      <Button variant="primary" disabled={fullList.length === 0} onClick={() => createCodeList(fullList, box)}>
        Download barcode list
      </Button>
    </ButtonGroup>

    <ButtonGroup>
      <Button variant='secondary' disabled={fullList.length === 0} onClick={() => createXMLnewparts(fullIDList, "Full-" + box)}>
        Export all new parts XML {Icons.notebook}
      </Button>
      <Button variant='success' disabled={fullList.length === 0} onClick={() => createXMLqcgrade(fullIDList, "Full-" + box)}>
        Export all QC grade XML {Icons.notebook}
      </Button>
    </ButtonGroup>
  </div>
}

const createBareQualityCache = (baseplateList, glueList) => {
  // We will create a cache results with the barcode as the lookup key whenever a draw element is called,
  // this reduces the requirement the expensive parse everytime
  const cache = {};
  if (!('id' in baseplateList)) return cache;

  const origin = (bp) => {
    if (bp.bare_barcode === null) { return "EU"; }
    if (bp.bare_barcode.substring(8, 10) === "II") { return "II"; }
    if (bp.bare_barcode.substring(8, 10) === "IH") { return "IH"; }
    return "EU";
  }

  for (let i = 0; i < baseplateList.id.length; ++i) {
    const bp = createRowObj(baseplateList, i);
    const warning = []
    if (bp.weight_bare === null) {
      warning.push("Missing manual measurements");
    }
    if (bp.radius0 === null) {
      warning.push("Missing 2D measurements");
    }
    if (bp.flatness_bare_l === null && bp.flatness_bare_r === null) {
      warning.push("Missing bare flatness");
    }
    if (bp.flatness_grade === "red") {
      warning.push("Red-grade flatness");
    }
    if (bp.tolerance_grade === "red" || !bp.diameter_hole_passed || !bp.width_slot_passed) {
      warning.push("Red-grade tolerance");
    }

    const glue_remote = findTableRow(glueList, (glue) => (glue.id === bp.glue_id) && (glue.source === "CERN" || glue.source === "SC"))
    if (glue_remote !== null) {
      warning.push("Already in process with local glue");
    }

    const cache_key = bp.bare_barcode ? bp.bare_barcode : bp.workshop_serial;
    // Returning the reduced item
    cache[cache_key] = {
      'id': bp.id, // Required for downstream processing
      'remotetask_id': bp.remotetask_id, // Remote is not known at the moment of caching
      'warning': warning,
      'origin': origin(bp),
    }
  }

  return cache;
}

const _validateRemoteList = (key_list, qualityCache, remotetask_id) => {
  var idList = []; // List of all ID that we are looking for
  const warningMessage = {}; // List of all potential issues

  const appendWarning = (message, key) => {
    if (!(message in warningMessage)) {
      warningMessage[message] = [];
    }
    warningMessage[message].push(key);
  }

  const originCount = { "II": 0, "IH": 0, "EU": 0 };

  for (const key of key_list) {
    if (key === "" || key === null) continue;
    const wkey = key.includes("BP") ? key : Number(key);
    if (!(wkey in qualityCache)) {
      appendWarning("Unknown baseplate", wkey);
      continue;
    }
    idList.push(qualityCache[wkey].id);
    for (const warn of qualityCache[wkey].warning) {
      appendWarning(warn, wkey)
    }
    if (qualityCache[wkey].remotetask_id !== null && qualityCache[wkey].remotetask_id !== remotetask_id) {
      appendWarning("Already assigned to other remote", wkey);
    }
    // Increment the number of origin
    originCount[qualityCache[wkey].origin]++;
  }
  // Reloop
  const maxOrigin = Object.keys(originCount).reduce(function(a, b) { return originCount[a] > originCount[b] ? a : b });
  for (const key of key_list) {
    if (key === "" || key === null) continue;
    if (!(key in qualityCache)) continue;
    if (qualityCache[key].origin !== maxOrigin) {
      appendWarning("Multiple origin detected", key)
    }
  }
  return [warningMessage, idList];
}

const validateRemoteList = (input, setBaseplateIDList, setWarningMessage, qualityCache, remotetask_id, glue_id) => {
  const [newWarn, newIdList] = _validateRemoteList(
    input.split(/[\s,]+/)
      .filter((t) => t !== "") // Remove blanks
      .filter((v, i, arr) => arr.indexOf(v) === i), // Striping to unique
    qualityCache, remotetask_id, glue_id
  );
  setWarningMessage(newWarn);
  setBaseplateIDList(newIdList);
}

const DisplayInputWarning = ({ found, warnings }) => {
  return <div>
    <Row>
      <Col>Found baseplates</Col>
      <Col>{found.length}</Col>
    </Row>
    {Object.keys(warnings).filter((key) => ((warnings[key].length > 0) && !key.endsWith("CMSR"))).map((key) =>
      <Fragment key={`fragment-${key}`}>
        <hr />
        <Row>
          {key}</Row>
        <Row>
          {warnings[key].map((bp) => `${bp}`).join(", ")}
        </Row>
      </Fragment>
    )}
  </div>
}

const RemoteAdd = ({ showState }) => {
  const { baseplateList, glueList, kaptonList, tapeList, updateBaseplateList, updateRemoteList } = useContext(DatabaseContext);
  const [, setShow] = showState;
  const [invalid, setInvalid] = useState("");
  const form = {
    "remote_desc": useState(null),
    "remote_date": useState(new Date()),
    "stt_idx": useState(null),
  }
  const bpForm = {
    "glue_id": useState(null),
    "kapton_id": useState(null),
    "tape_id": useState(null),
  }
  const [idList, setIDList] = useState([]) // For to be passed to the database API
  const [baseplateInput, setBaseplateInput] = useState("") // to be used for element displays
  // eslint-disable-next-line
  const errorForm = {
    "remote_desc": useState(""),
    "glue_id": useState(""),
    "kapton_id": useState(""),
    "stt_idx": useState(""),
  };
  const [baseplateInputWarning, setBaseplateInputWarning] = useState({});

  const resetAction = () => {
    defaultResetAdd(form, { 'remote_date': () => new Date() })();
    defaultResetAdd(bpForm)();
    setIDList([]);
    setBaseplateInput("");
  }
  const addActionStep1 = defaultAdd(form, addRemote, updateRemoteList, resetAction, setShow);
  const addAction = async () => {
    const newRemote = (await addActionStep1());
    await updateBPBatch({
      'idList': idList,
      ...makeBPUpdateForm(newRemote.id, bpForm.glue_id[0], bpForm.kapton_id[0], bpForm.tape_id[0])
    });
    await updateBaseplateList();
    await updateRemoteList();
    window.location.reload(false);
  }

  useEffect(() => { setInvalid(defaultValidateAdd(errorForm)) }, [errorForm])
  const qualityCache = createBareQualityCache(baseplateList, glueList);

  //eslint-disable-next-line
  const handleInputField = useCallback(
    debounce((input) => { validateRemoteList(input, setIDList, setBaseplateInputWarning, qualityCache, null) }, 200)
    , [baseplateInput])

  return <TableViewAdd title="Add new remote process" showState={showState} addAction={addAction} invalid={invalid}>
    <HGCFormRowText label="Box-ID" dataState={form["remote_desc"]} errorState={errorForm["remote_desc"]} validate={validateAlphanumeric(true)} autoFocus={true} />
    <HGCFormRowDate label="Remote shipment date" dataState={form["remote_date"]} />
    <HGCFormRowSelect label="Remote location" dataState={bpForm["glue_id"]} options={makeRemoteGlueOptions(glueList)} errorState={errorForm["glue_id"]} allowNull={false} />
    <HGCFormRowSelect label="Kapton ID" dataState={bpForm["kapton_id"]} options={makeKaptonOptions(kaptonList)} errorState={errorForm["kapton_id"]} allowNull={false} />
    <HGCFormRowSelect label="Tape ID" dataState={bpForm["tape_id"]} options={makeTapeOptions(tapeList)} />
    <HGCFormRowText label="CMSR STT index" dataState={form["stt_idx"]} errorState={errorForm["stt_idx"]} validate={validatePositiveInt(false)} />
    <Row style={{ marginBottom: "1.5em" }}>
      <Form.Label column sm={3}>Workshop serial/Bare barcodes</Form.Label>
      <Col sm={3}>
        <FormControl
          text="text"
          as="textarea"
          value={baseplateInput}
          rows={30}
          style={{ textSizeAdjust: "50%" }}
          onChange={(e) => {
            setBaseplateInput(e.target.value);
            handleInputField(e.target.value);
          }}
        />
      </Col>
      <Col sm={4}>
        <DisplayInputWarning found={idList} warnings={baseplateInputWarning} />
      </Col>
    </Row>
  </TableViewAdd >
}

const RemoteEdit = ({ id, showState }) => {
  const { baseplateList, glueList, kaptonList, tapeList, remoteList, updateBaseplateList, updateRemoteList } = useContext(DatabaseContext);
  const [, setShow] = showState;
  const remote = findTableRow(remoteList, (remote) => remote.id === id);
  const origIDList = remote.idlist.filter((v, i) => (v !== null) && (remote.bare_list[i] !== null || remote.ws_list[i] !== null));
  const origKeyList = makeKeyList(remote).sort();
  const origAssemblyIDs = extractIDs(remote, baseplateList, kaptonList, tapeList);
  const [invalid, setInvalid] = useState("");
  const form = {
    "remote_desc": useState(null),
    "remote_date": useState(new Date()),
    "stt_idx": useState(remote.stt_idx),
  }
  const bpForm = {
    "glue_id": useState(origAssemblyIDs.glue_id),
    "kapton_id": useState(origAssemblyIDs.kapton_id),
    "tape_id": useState(origAssemblyIDs.tape_id),
  }
  const [idList, setIDList] = useState(origIDList);
  const [baseplateInput, setBaseplateInput] = useState(origKeyList.join("\n")) // to be used for element displays
  // eslint-disable-next-line
  const errorForm = {
    "remote_desc": useState(""),
    "glue_id": useState(""),
    "kapton_id": useState(""),
    "stt_idx": useState(""),
    "baseplateInput": useState(""),
  };
  const [baseplateInputWarning, setBaseplateInputWarning] = useState({});

  const resetActionStep1 = defaultReset(form, remote)
  const resetAction = () => {
    resetActionStep1();
    const origIDList = remote.idlist;
    const origKeyList = makeKeyList(remote);
    setIDList(origIDList);
    bpForm.glue_id[1](origAssemblyIDs.glue_id);
    bpForm.kapton_id[1](origAssemblyIDs.kapton_id);
    bpForm.tape_id[1](origAssemblyIDs.tape_id);
    setBaseplateInput(origKeyList.join("\n"))
    validateRemoteList(origKeyList.join("\n"), setIDList, setBaseplateInputWarning, qualityCache, id); // Run once each time th reset is defined
  }
  const editActionStep1 = defaultEdit(id, form, updateRemote, updateRemoteList, resetAction, setShow);
  const editAction = async () => {
    editActionStep1();
    await updateBPBatch({
      'idList': origIDList,
      ...makeBPUpdateForm(),
    }); // Must be in this order
    await updateBPBatch({
      'idList': idList,
      ...makeBPUpdateForm(id, bpForm.glue_id[0], bpForm.kapton_id[0], bpForm.tape_id[0]),
    });
    updateBaseplateList();
    updateRemoteList();
    setShow(false);
    window.location.reload(false);
  }
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  const qualityCache = createBareQualityCache(baseplateList, glueList);
  //eslint-disable-next-line
  const handleInputField = useCallback(
    debounce((input) => { validateRemoteList(input, setIDList, setBaseplateInputWarning, qualityCache, id) }, 200)
    , [baseplateList])
  //eslint-disable-next-line
  useEffect(() => validateRemoteList(baseplateInput, setIDList, setBaseplateInputWarning, qualityCache, id))

  return <TableViewEdit title="Edit remote details" showState={showState} editAction={editAction} resetAction={resetAction} invalid={invalid}>
    <HGCFormRowText label="Box-ID" dataState={form["remote_desc"]} errorState={errorForm["remote_desc"]} validate={validateAlphanumeric(true)} />
    <HGCFormRowDate label="Remote shipment date" dataState={form["remote_date"]} />
    <HGCFormRowSelect label="Remote location" dataState={bpForm["glue_id"]} options={makeRemoteGlueOptions(glueList)} errorState={errorForm["glue_id"]} allowNull={false} />
    <HGCFormRowSelect label="Kapton ID" dataState={bpForm["kapton_id"]} options={makeKaptonOptions(kaptonList)} errorState={errorForm["kapton_id"]} allowNull={false} />
    <HGCFormRowSelect label="Tape ID" dataState={bpForm["tape_id"]} options={makeTapeOptions(tapeList)} />
    <HGCFormRowText label="CMSR STT index" dataState={form["stt_idx"]} errorState={errorForm["stt_idx"]} validate={validatePositiveInt(false)} />
    <Row style={{ marginBottom: "1.5em" }}>
      <Form.Label column sm={3}>CMS bar codes</Form.Label>
      <Col sm={3}>
        <FormControl
          text="text"
          as="textarea"
          value={baseplateInput}
          rows={30}
          style={{ textSizeAdjust: "50%" }}
          onChange={(e) => {
            setBaseplateInput(e.target.value);
            handleInputField(e.target.value);
          }}
        />
      </Col>
      <Col sm={4}>
        <DisplayInputWarning found={idList} warnings={baseplateInputWarning} />
      </Col>
    </Row>
  </TableViewEdit>
}

export const RemoteList = () => {
  const { remoteList } = useContext(DatabaseContext);
  const [showAdd, setShowAdd] = useState(false);

  return <ProtectedComponent>
    <Button onClick={() => { setShowAdd(true) }}>(+) Create new remote processing</Button>
    <RemoteAdd showState={[showAdd, setShowAdd]} />
    <hr />
    <PaginateTable tableData={remoteList} columnLayout={RemoteColumns()} />
  </ProtectedComponent>
}

export default RemoteList;
